export const CUSTOMER_EVIDENCE_MEDIA_ROLE = "customer_evidence";
export const RETURN_MEDIA_MAX_FILE_SIZE_BYTES = 15000000;

const toNonNegativeInteger = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.floor(number)) : 0;
};

export function getMediaRequirementMinimum(config = {}) {
  const configuredMinimum = toNonNegativeInteger(config.min_count);
  return config.required ? Math.max(1, configuredMinimum) : configuredMinimum;
}

export function getMediaRequirementMaximum(config = {}) {
  const minimum = getMediaRequirementMinimum(config);
  const configuredMaximum = toNonNegativeInteger(config.max_count);
  return Math.max(1, minimum, configuredMaximum);
}

export function getMediaRequirementKey(config = {}, index = 0) {
  if (config.id !== undefined && config.id !== null && config.id !== "") {
    return String(config.id);
  }

  return [
    "legacy",
    index,
    config.media_type || "media",
    config.capture_code || "other",
    config.sort_order || 0,
  ].join(":");
}

export function getReturnMediaFileValidationError(file = {}, mediaType) {
  const fileName = file.name || "Selected file";
  const fileType = String(file.type || "").toLowerCase();

  if (!file.size) {
    return `${fileName} is empty and cannot be uploaded.`;
  }
  if (!fileType.startsWith(`${mediaType}/`)) {
    return `${fileName} is not a supported ${mediaType} file.`;
  }
  if (fileType === "image/heic" || fileType === "image/heif") {
    return `File ${fileName} is a HEIC file and will not be uploaded.`;
  }
  if (file.size > RETURN_MEDIA_MAX_FILE_SIZE_BYTES) {
    return `${fileName} is too large. Max file size is 15MB.`;
  }

  return null;
}

export function isReturnMediaUploadForConfig(file = {}, config = {}, index = 0) {
  const configKey = String(
    config.upload_key || getMediaRequirementKey(config, index),
  );
  const uploadConfigId = file?.media_config_id;

  if (
    uploadConfigId !== undefined &&
    uploadConfigId !== null &&
    uploadConfigId !== ""
  ) {
    return String(uploadConfigId) === configKey;
  }

  const legacyConfigKey = `legacy-${config.media_type}`;
  return (
    configKey === legacyConfigKey &&
    String(file?.type || "").startsWith(`${config.media_type}/`)
  );
}

export function areReturnMediaRequirementsMet(
  mediaConfigs = [],
  uploads = [],
) {
  if (!Array.isArray(mediaConfigs) || mediaConfigs.length === 0) {
    return true;
  }

  const availableUploads = Array.isArray(uploads) ? uploads : [];
  return mediaConfigs.every((config, configIndex) => {
    const minimum = getMediaRequirementMinimum(config);
    return (
      availableUploads.filter((file) =>
        isReturnMediaUploadForConfig(file, config, configIndex),
      ).length >= minimum
    );
  });
}

export function reasonRequiresMedia(reason = {}) {
  return Boolean(
    reason?.is_media_required ||
      (Array.isArray(reason?.media_configs) && reason.media_configs.length > 0),
  );
}

export function hasReturnMediaReasonIdentity(reason = {}) {
  return Boolean(reason?.id || reason?.reason_code);
}

export function getReasonMinimumConfiguredMediaUploads(reason = {}) {
  const mediaConfigs = Array.isArray(reason?.media_configs)
    ? reason.media_configs
    : [];

  if (mediaConfigs.length === 0) {
    return 0;
  }

  const configuredMinimum = mediaConfigs.reduce(
    (total, config) => total + getMediaRequirementMinimum(config),
    0,
  );

  return Math.max(1, configuredMinimum);
}

export function areOptionalReturnMediaConfigsCollectivelyMissing(
  mediaConfigs = [],
  uploads = [],
) {
  return (
    Array.isArray(mediaConfigs) &&
    mediaConfigs.length > 0 &&
    mediaConfigs.every((config) => getMediaRequirementMinimum(config) === 0) &&
    (!Array.isArray(uploads) || uploads.length === 0)
  );
}

export function getReasonMinimumMediaUploads(reason = {}, mediaType) {
  const mediaConfigs = Array.isArray(reason?.media_configs)
    ? reason.media_configs
    : [];

  if (mediaConfigs.length > 0) {
    return mediaConfigs
      .filter((config) => config.media_type === mediaType)
      .reduce(
        (total, config) => total + getMediaRequirementMinimum(config),
        0,
      );
  }

  if (!reason?.is_media_required) {
    return 0;
  }

  if (mediaType === "image") {
    const minimumPhotos = toNonNegativeInteger(reason.min_number_of_photos);
    const minimumVideos = toNonNegativeInteger(reason.min_number_of_videos);
    return minimumPhotos > 0 || minimumVideos === 0
      ? Math.max(1, minimumPhotos)
      : 0;
  }

  if (mediaType === "video") {
    return toNonNegativeInteger(reason.min_number_of_videos);
  }

  return 0;
}

export function restorePersistedReturnMediaUpload(mediaItem = {}) {
  return {
    ...mediaItem,
    name: mediaItem.name || String(mediaItem.media || ""),
    type:
      mediaItem.type ||
      (mediaItem.url?.match(/\.(mp4|mov|webm)(\?|$)/i)
        ? "video/mp4"
        : "image/jpeg"),
    persisted: true,
  };
}

export function hasReturnMediaUploads(lineItemMedia = []) {
  return Array.isArray(lineItemMedia) && lineItemMedia.some(
    (item) =>
      Array.isArray(item?.uploads) &&
      item.uploads.some(
        (upload) => upload?.media !== undefined && upload?.media !== null,
      ),
  );
}

export function getScopedReturnMediaIds(lineItemMedia = [], lineItems = []) {
  const selectedLineItemIds = new Set(
    (Array.isArray(lineItems) ? lineItems : [])
      .map((item) => String(item?.line_item_id ?? "").trim())
      .filter(Boolean),
  );
  const mediaIds = [];
  const seenMediaIds = new Set();

  (Array.isArray(lineItemMedia) ? lineItemMedia : []).forEach((item) => {
    const lineItemId = String(item?.line_item_id ?? "").trim();
    if (!selectedLineItemIds.has(lineItemId)) {
      return;
    }

    (Array.isArray(item?.uploads) ? item.uploads : []).forEach((upload) => {
      const mediaId = upload?.media;
      if (mediaId === undefined || mediaId === null || mediaId === "") {
        return;
      }

      const mediaKey = String(mediaId);
      if (!seenMediaIds.has(mediaKey)) {
        seenMediaIds.add(mediaKey);
        mediaIds.push(mediaId);
      }
    });
  });

  return mediaIds;
}

export function buildReturnMediaPresignFiles(signedUploads, lineItemId) {
  const sourceLineItemId = String(lineItemId ?? "").trim();

  if (!sourceLineItemId) {
    throw new Error("A Shopify line item ID is required to upload return media.");
  }

  return signedUploads.map(({ file, requestName }) => ({
    name: requestName,
    content_type: file.type,
    source_line_item_id: sourceLineItemId,
    capture_code: String(file.capture_code || "other").trim() || "other",
    media_role: CUSTOMER_EVIDENCE_MEDIA_ROLE,
  }));
}
